import axios from 'axios';
import { POLL_REQUEST_TIMEOUT_MS, pollJob } from './job-polling';
import {
  WorksheetFormData,
  WorksheetJobProgress,
  WorksheetJobResponse,
  WorksheetJobStatusResponse
} from '../models/worksheet.types';

const API_BASE_URL = process.env.REACT_APP_API_SERVER_URL || 'http://localhost:6060';

/**
 * Async worksheet generation service.
 *
 * Mirrors SlideshowService: POST /api/worksheets/generate returns immediately
 * with a job_id; GET /api/worksheets/status/{job_id} is polled until the job
 * completes or fails. See docs/worksheet_contract.md.
 */
export class WorksheetService {
  /**
   * Start worksheet generation. Returns a job id immediately (202 Accepted).
   */
  static async generateWorksheet(
    formData: WorksheetFormData,
    accessToken: string
  ): Promise<WorksheetJobResponse> {
    const response = await axios.post(
      `${API_BASE_URL}/api/worksheets/generate`,
      { formData },
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        // Generation kicks off an async job and returns 202 quickly, so a
        // hung backend here is a real problem, not an in-progress generation.
        timeout: 15000
      }
    );
    return response.data;
  }

  /**
   * Fetch the current status of a worksheet generation job.
   */
  static async getJobStatus(
    jobId: string,
    accessToken: string,
    signal?: AbortSignal
  ): Promise<WorksheetJobStatusResponse> {
    const response = await axios.get(
      `${API_BASE_URL}/api/worksheets/status/${jobId}`,
      {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        },
        // Each poll is independent; if one hangs we'd rather drop it than
        // block the loop indefinitely. Must exceed worst-case Lambda cold
        // start (~10s) or every poll that lands on a cold container fails.
        timeout: POLL_REQUEST_TIMEOUT_MS,
        signal
      }
    );
    return response.data;
  }

  /**
   * Poll a worksheet job until it completes or fails.
   *
   * @param jobId        Job identifier returned by generateWorksheet.
   * @param accessToken  Auth0 access token.
   * @param onProgress   Called with each progress update while polling.
   * @param pollInterval Milliseconds between polls.
   * @param maxAttempts  Maximum polls before timing out.
   * @param signal       AbortSignal — callers should abort on unmount so we
   *                     don't keep polling for ~4 minutes after the user
   *                     navigates away (§4.2).
   */
  static async pollJobStatus(
    jobId: string,
    accessToken: string,
    onProgress?: (progress: WorksheetJobProgress) => void,
    pollInterval: number = 2000,
    maxAttempts: number = 120,
    signal?: AbortSignal
  ): Promise<WorksheetJobStatusResponse> {
    return pollJob<WorksheetJobStatusResponse>({
      fetchStatus: () => this.getJobStatus(jobId, accessToken, signal),
      isTerminal: (status) => status.status === 'completed' || status.status === 'failed',
      onStatus: (status) => {
        if (onProgress && status.progress) {
          onProgress(status.progress);
        }
      },
      timeoutMessage: 'Worksheet generation timed out. Please try again.',
      pollInterval,
      maxAttempts,
      signal
    });
  }
}
